<script setup lang="ts">
import { usePage, Head, Link, useForm, router } from '@inertiajs/vue3';
import { trans } from 'laravel-vue-i18n';
import { getCurrentInstance, ref, defineComponent, onMounted, computed, watch } from 'vue';
import NavBar from '@/Components/NavBar.vue';
import CountdownTimer from '@/Components/CountdownTimer.vue';
import PhoneInput from '@/Components/PhoneInput.vue';
import InputError from '@/Components/InputError.vue';
import InputLabel from '@/Components/InputLabel.vue';
import TextInput from '@/Components/TextInput.vue';

import 'vue3-carousel/dist/carousel.css'
import { Carousel, Slide, Pagination, Navigation } from 'vue3-carousel'
import axios from 'axios';
import InputOTP from '@/Components/InputOTP.vue';

const page = usePage()
const locale = page.props.locale;
const instance = getCurrentInstance();
const global = instance.appContext.config.globalProperties
const isLoggedIn = page.props.auth.user !== null;
const room = page.props.room;
const ratings = page.props.ratings;

const show = ref('');
const showLeranMore = ref(false);
const showWinners = ref(false);
const showLogin = ref(false);
const showVerifyCode = ref(false);
const showRegister = ref(false);
const showSearch = ref(false);
const mobileNumber = ref(null);
const filteredFAQ = ref([]);

const isMobile = computed(() => {
  return screen.width <= 760;
});

defineComponent({
  name: 'Basic',
  components: {
    Carousel,
    Slide,
    Pagination,
    Navigation,
  },
})

const collapseId = ref(0);
const rooms = ref<HTMLElement | null>(null)

function handleImageError() {
    document.getElementById('screenshot-container')?.classList.add('!hidden');
    document.getElementById('docs-card')?.classList.add('!row-span-1');
    document.getElementById('docs-card-content')?.classList.add('!flex-row');
    document.getElementById('background')?.classList.add('!hidden');
}

function scrollTo(id) {
    const el = document.getElementById(id)    
    global.$smoothScroll({
      scrollTo: el, // scrollTo is also allowed to be number
    //   hash: '#sampleHash' // required if updateHistory is true
    })
}

function toggle(id, value) {

    if(id == 'learn-more') {
        showLeranMore.value = value;
    } else if(id == 'winners') {
        showWinners.value = value;
    } else if(id == 'login') {
        showLogin.value = value;
        formLogin.processing = false;
    } else if(id == 'verify-code') {
        showVerifyCode.value = value
        formVerifyCode.processing = false;
    } else if(id == 'register') {
        showRegister.value = value
        formRegister.processing = false;
    } else if(id == 'search') {
        showSearch.value = value;
    }
    
    if(value == false) {
        show.value = '';
    } else {
        show.value = id
    }
} 

onMounted(() => {
    if(global.$page.url.includes('#rooms')) {
        showLeranMore.value = false;
    }    
});

const formLogin = useForm({
    mobile_number: '',
});

const formVerifyCode = useForm({
    mobile_number: '',
    code: '',
});
const formRegister = useForm({
    first_name: '',
    last_name: '',
    mobile_number: '',
    gender: '',
    age: '',
});
const formSearch = useForm({
    search: '',
});

const submitLogin = () => {

    var errorsCount = 0;
    
    hideErrorsMessage();

    var isValidMobileNumber = formLogin.mobile_number.match(/^(5)(5|0|3|6|4|9|1|8|7)([0-9]{7})$/)

    if(!isValidMobileNumber) {
        formLogin.errors.mobile_number = getErrorWithFormat('5xxxxxxxx');
        errorsCount += 1;
    }

    if(errorsCount > 0) {
        return;
    }

    formLogin.processing = true;
    axios.post(route('user.auth.login'), formLogin)
    .then(res => {
        formVerifyCode.mobile_number = res.data['mobile_number']
        showLogin.value = false;
        showVerifyCode.value = res.data['show_verification_code'];
        if(res.data['errors']['code'] !== null) {
            formVerifyCode.errors.code = res.data['errors']['code']
        }
    }).catch(error => {
        formLogin.errors.mobile_number = error.response.data.errors.mobile_number
    });
};

const submitVerifyCode = () => {

    var errorsCount = 0;
    
    formVerifyCode.errors.code = null;

    var isValidCode = formVerifyCode.code.toString().length == 4

    if(!isValidCode) {
        formVerifyCode.errors.code = 'The code must be 4 number.';
        errorsCount += 1;
    }
    
    if(errorsCount > 0) {
        return;
    }

    formVerifyCode.processing = true;

    axios.post(route('user.auth.verify-code'), formVerifyCode)
    .then(res => {
        const isRegistered = res.data['is_registered']
        if(!isRegistered) {
            showLogin.value = false;
            showVerifyCode.value = false;
            showRegister.value = true;
        } else {
            window.location.reload()
        }

    }).catch(error => {
        formVerifyCode.processing = false;
        formVerifyCode.errors.code = error.response.data.errors.code
    });
};

const submitRegister = () => {

    var errorsCount = 0;
    
    hideErrorsMessage();

    if(formRegister.first_name.length == 0) {
        formRegister.errors.first_name = trans('Required');
        errorsCount += 1;
    }

    if(formRegister.last_name.length == 0) {
        formRegister.errors.last_name = trans('Required');
        errorsCount += 1;
    }

    if(formRegister.age.toString().length > 2) {
        formRegister.errors.age = trans('The entered age is invalid');
        errorsCount += 1;
    }

    if(errorsCount > 0) {
        return;
    }

    axios.post(route('user.auth.register'), {
        first_name: formRegister.first_name,
        last_name: formRegister.last_name,
        mobile_number: formVerifyCode.mobile_number,
        gender: formRegister.gender,
        age: formRegister.age,
    })
    .then(res => {
        window.location.reload()
    })
};

function getErrorWithFormat(format) {
    return trans('The input must be in the following format :format', {format: format});
}

function hideErrorsMessage() {
    for (const [key, value] of Object.entries(formLogin.errors)) {
        formLogin.errors[key] = null;
    }
}

function resendOTP() {
    axios.post(route('user.auth.resend-otp'), {
        mobile_number: formLogin.mobile_number
    })
    .then(res => {
        global.$alerts.toast(
            res.data.message
        );
    }).catch(error => {
        formVerifyCode.errors.code = error.response.data.errors.code
    });
}

function getMobileNumber(mobileNumber) {
    var firstPart = mobileNumber.substr(0,5);
    var lastPart = mobileNumber.substr(11, mobileNumber.length);
    return firstPart.slice(1) + 'xxxxx' + lastPart;
}

function showRoom(room) {
    console.log(global);
    
    if(!page.props.auth.user) {
        showLogin.value = true;
        return;
    }

    if(!room.release_date) {
        router.get(route('user.book.room', {id: room.id}));
    }
}

watch(() => formSearch.search, (query) => {
    console.log(query.length);
    
    if(query.length == 0) {
        filteredFAQ.value = []
    } else {
        filteredFAQ.value = page.props.faq.filter(item => `${item.question}`.includes(query));
    }
});

</script>
<style>
#overlay {
  background: radial-gradient(transparent, black);
}
</style>
<style>

.carousel__icon {
    color: white;
}
.carousel__pagination-button::after {
    background-color: gray;
}

.carousel__prev:hover, .carousel__next:hover {
    color: white;
}
.carousel__pagination {
    align-items: center !important;
}
.carousel__pagination > * {
    padding: 0 5px;
}
.carousel__pagination-button--active::after {
    background-color: white;
}
.carousel__pagination-button::after {
    width: 10px;
    height: 10px;
    border-radius: 10px;
    background-color: #929292;
}
.carousel__pagination-button .carousel__pagination-button--active::after {
    color: white;
}
.carousel__pagination-button--active::after {
    display: block;
    content: '';
    width: 30px;
    height: 30px;
    border-radius: 20px;
    border: 10px solid white;
    background-color: transparent !important;
    /* background-color: var(--vc-pgn-background-color); */
}
@media screen and (min-width: 768px) {

    .carousel__slide {
      padding: 5px;
    }
    
    .carousel__viewport {
      perspective: 2000px;
    }
    
    .carousel__track {
      transform-style: preserve-3d;
    } 
    .carousel__viewport {
        padding: 50px 0px !important;
    }
    
    .carousel__slide--sliding {
      transition: 0.5s;
    }
    .carousel__item {
        width: 100% !important;
    }    
    .carousel__slide--active ~ .carousel__slide {
      transform: rotateY(20deg) scale(0.9);
    }
    
    .carousel__slide--prev {
      opacity: 0.3;
      transform: rotateY(-10deg) scale(0.8) !important;
    }
    
    .carousel__slide--next {
      opacity: 0.3;
      transform: rotateY(10deg) scale(0.8) !important;
    }
    
    .carousel__slide--active {
      opacity: 1;
      transform: rotateY(0) scale(1.5) !important;
    }
}
@media screen and (max-width: 768px) {
    .carousel__item {
        width: 300px !important;
    }
}
@media (hover: hover) {
    .carousel__pagination-button:hover::after {
        background-color: #656565;
    }
}
</style>

<template>
    <div class="relative">
        <template v-if="page.props.settings.top_bar_message_title">
            <div class="bg-red-700 p-4 font-bold text-center text-white">
                <p>{{ page.props.settings.top_bar_message_title }}</p>
                <p v-if="page.props.settings.top_bar_message_subtitle">{{ page.props.settings.top_bar_message_subtitle }}</p>
            </div>
        </template>

        <div :style="{
            '-webkit-box-shadow': 'inset 0 0 200px #000000',
            '-moz-box-shadow': 'inset 0 0 200px #000000',
            'box-shadow': 'inset 0 0 200px #000000',}">
            <div class="relative text-gray-900 antialiased bg-white border-gray-200 dark:bg-gray-900 bg-top" :style="{backgroundImage: 'radial-gradient(transparent, black),    url(\'/assets/images/' + page.props.settings.header_background +'\')', backgroundRepeat: 'no-repeat', backgroundAttachment: 'fixed', '-webkit-filter': showLeranMore || showWinners || showLogin || showVerifyCode || showRegister || showSearch ? 'blur(8px)' : ''}">
        
                <!-- Header -->
                <header class="col-start-1 row-start-1 w-full h-screen px-4 md:px-16">
                    
                    <NavBar @scrollTo="scrollTo" @toggle="toggle" :show="show"/>
        
                    <div class="md:flex align-middle items-center justify-between my-20">
                        <div>
                            <p class="text-4xl font-bold text-white whitespace-pre">{{ page.props.settings.header_title }}</p>
                            <p class="text-base font-bold text-slate-400">{{ page.props.settings.header_subtitle }}</p>
                            <button @click="scrollTo('rooms')" class="relative inline-flex items-center justify-center p-0.5 mt-4 mb-2 me-2 overflow-hidden text-sm font-medium text-slate-400 rounded-lg group bg-transparent border border-slate-400 hover:text-slate-100 hover:border-slate-100">
                                <span class="relative px-2 py-1 transition-all ease-in duration-75  dark:bg-gray-900 rounded-md group-hover:bg-opacity-0">
                                    {{ $t('Browse rooms') }}
                                </span>
                            </button>
                        </div>
        
                        <div v-if="room">
                            <div class="relative max-w-sm">
                                <!-- <div id="overlay" class="absolute w-full h-full"></div> -->
                                
                                <button @click="showRoom(room)">
                                    <img :src="'/assets/rooms/' + room.locale_image" :alt="room.locale_image" class="h-[480px] w-full object-cover rounded-lg cursor-pointer">
                                </button>
                                <div class="absolute bottom-0 text-center p-4 rounded-lg" :style="{background: 'rgb(2,0,36)', background: 'linear-gradient(0deg, black, transparent)'}">
                                    <p class="text-3xl font-bold text-white">{{ JSON.parse(room.name)[locale] }}</p>
                                    <p class="text-white">{{ JSON.parse(room.description)[locale] }}</p>
                                </div>
                                
                            </div>
                            <CountdownTimer v-if="room"/>
                        </div>
        
                    </div>

                </header>
                <!-- End Header -->
        
                <!-- Rooms -->
                <section id="rooms" class="relative h-full text-center mt-28 md:mt-0">
                    <div class="p-16 space-y-6">
                        <p class="text-6xl font-bold text-white">{{ trans('Rooms') }}</p>
                        <div class="grid grid-cols-1 md:grid-cols-4 gap-4 justify-center items-center text-white text-sm leading-6">
                            <button v-for="item in page.props.rooms" @click="showRoom(item)"  class="transform transition duration-300 hover:scale-105 cursor-pointer">
                                <div class="relative">
                                    <img :src="'/assets/rooms/' + item.locale_image" :alt="item.locale_image" class="h-[480px] w-full object-cover rounded-lg cursor-pointer">
                                    <div class="w-full h-full bg-black opacity-0 hover:opacity-35 absolute bottom-0 transform transition duration-300 rounded-lg"></div>
                                    <div class="absolute bottom-0 flex items-end w-full h-full p-4 rounded-lg" :style="{background: 'rgb(2,0,36)', background: 'linear-gradient(0deg, black, transparent)'}">
                                        <div>
                                            <p class="text-3xl text-white hover:text-slate-200 font-bold mt-2 text-center">{{ JSON.parse(item.name)[locale] }}</p>
                                            <p>{{ JSON.parse(item.description)[locale] }}</p>
                                        </div>
                                    </div>
                                </div>
                            </button>
                        </div>
                    </div>
                </section>
                <!-- End Rooms -->
                
                <!-- Footer -->
                <footer class="bg-[#111] dark:bg-slate-800 pt-4">
        
                    <div class="max-w-screen-xl mx-auto h-max p-4">
                        <div class="md:flex md:justify-between">
                        <div class="mb-8 md:mb-0 max-w-xs">
                            <a href="#" class="flex items-center mb-2 space-x-2 rtl:space-x-reverse">
                                <img src="assets/images/escape_logo.png" alt="Smartista Logo" class="h-8" />
                                <span class="self-center text-2xl font-semibold whitespace-nowrap text-white">{{ page.props.settings.site_name }}</span>
                            </a>
                            <P class="text-white">{{ page.props.settings.site_description }}</P>
                        </div>
                        <div class="md:grid grid-cols-4 gap-8 sm:gap-6 sm:grid-cols-3">
                            <div class="flex relative col-span-2 h-[180px] md:h-full">
                                <a href="https://maps.app.goo.gl/Xcm1uKyBrSXynVdu7" class="absolute w-full h-[full]">
                                    <iframe class="w-full rounded-lg" src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d907.8292504784991!2d39.646708969661695!3d24.474470898636472!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x15bdbfe4a27f957f%3A0xe947d8fff67aa0b2!2zRXNjYXBlIHRpbWUgLSDZiNmC2Kog2KfZhNmH2LHZiNio!5e0!3m2!1sen!2ssa!4v1723069965458!5m2!1sen!2ssa" style="border:0;" allowfullscreen="" loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe>                        
                                </a>                        
                            </div>
                            <div>
                                <h2 class="mb-2 text-sm font-semibold text-white">{{ $t('Contact US') }}</h2>
                                <ul class="text-white font-medium">
                                    <li class="mb-4">
                                    <a href="mailto:esc@escape-p.com" class="hover:underline">
                                        <div class="flex space-x-2 rtl:space-x-reverse">
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" class="w-4 h-4" viewBox="0 0 16 16">
                                                <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4Zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1H2Zm13 2.383-4.708 2.825L15 11.105V5.383Zm-.034 6.876-5.64-3.471L8 9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741ZM1 11.105l4.708-2.897L1 5.383v5.722Z"/>
                                            </svg>
                                            <span>esc@escape-p.com</span>
                                        </div>
                                    </a>
                                    </li>
                                    <li class="mb-4">
                                    <a href="tel:+966540448875" class="hover:underline">
                                        <div class="flex space-x-2 rtl:space-x-reverse">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="w-4 h-4" viewBox="0 0 16 16">
                                                <path d="M3.654 1.328a.678.678 0 0 0-1.015-.063L1.605 2.3c-.483.484-.661 1.169-.45 1.77a17.568 17.568 0 0 0 4.168 6.608 17.569 17.569 0 0 0 6.608 4.168c.601.211 1.286.033 1.77-.45l1.034-1.034a.678.678 0 0 0-.063-1.015l-2.307-1.794a.678.678 0 0 0-.58-.122l-2.19.547a1.745 1.745 0 0 1-1.657-.459L5.482 8.062a1.745 1.745 0 0 1-.46-1.657l.548-2.19a.678.678 0 0 0-.122-.58L3.654 1.328zM1.884.511a1.745 1.745 0 0 1 2.612.163L6.29 2.98c.329.423.445.974.315 1.494l-.547 2.19a.678.678 0 0 0 .178.643l2.457 2.457a.678.678 0 0 0 .644.178l2.189-.547a1.745 1.745 0 0 1 1.494.315l2.306 1.794c.829.645.905 1.87.163 2.611l-1.034 1.034c-.74.74-1.846 1.065-2.877.702a18.634 18.634 0 0 1-7.01-4.42 18.634 18.634 0 0 1-4.42-7.009c-.362-1.03-.037-2.137.703-2.877L1.885.511z"/>
                                            </svg>                                        
                                            <span style="direction: ltr;">+966540448875</span>
                                        </div>
                                    </a>
                                    </li>
                                </ul>
                            </div>
                        </div>
                        </div>
                        <hr class="my-6 border-gray-200 sm:mx-auto dark:border-gray-700 lg:my-8" />
                        <div class="sm:flex sm:items-center sm:justify-between space-y-6 sm:space-y-0">
                            <div class="flex mt-4 space-x-5 sm:justify-center sm:mt-0 rtl:space-x-reverse">
                                <a href="https://snapchat.com/add/esc-plan" target="_blank" class="text-white hover:text-stone-300">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="currentColor" viewBox="0 0 16 16">
                                        <path d="M15.943 11.526c-.111-.303-.323-.465-.564-.599a1 1 0 0 0-.123-.064l-.219-.111c-.752-.399-1.339-.902-1.746-1.498a3.4 3.4 0 0 1-.3-.531c-.034-.1-.032-.156-.008-.207a.3.3 0 0 1 .097-.1c.129-.086.262-.173.352-.231.162-.104.289-.187.371-.245.309-.216.525-.446.66-.702a1.4 1.4 0 0 0 .069-1.16c-.205-.538-.713-.872-1.329-.872a1.8 1.8 0 0 0-.487.065c.006-.368-.002-.757-.035-1.139-.116-1.344-.587-2.048-1.077-2.61a4.3 4.3 0 0 0-1.095-.881C9.764.216 8.92 0 7.999 0s-1.76.216-2.505.641c-.412.232-.782.53-1.097.883-.49.562-.96 1.267-1.077 2.61-.033.382-.04.772-.036 1.138a1.8 1.8 0 0 0-.487-.065c-.615 0-1.124.335-1.328.873a1.4 1.4 0 0 0 .067 1.161c.136.256.352.486.66.701.082.058.21.14.371.246l.339.221a.4.4 0 0 1 .109.11c.026.053.027.11-.012.217a3.4 3.4 0 0 1-.295.52c-.398.583-.968 1.077-1.696 1.472-.385.204-.786.34-.955.8-.128.348-.044.743.28 1.075q.18.189.409.31a4.4 4.4 0 0 0 1 .4.7.7 0 0 1 .202.09c.118.104.102.26.259.488q.12.178.296.3c.33.229.701.243 1.095.258.355.014.758.03 1.217.18.19.064.389.186.618.328.55.338 1.305.802 2.566.802 1.262 0 2.02-.466 2.576-.806.227-.14.424-.26.609-.321.46-.152.863-.168 1.218-.181.393-.015.764-.03 1.095-.258a1.14 1.14 0 0 0 .336-.368c.114-.192.11-.327.217-.42a.6.6 0 0 1 .19-.087 4.5 4.5 0 0 0 1.014-.404c.16-.087.306-.2.429-.336l.004-.005c.304-.325.38-.709.256-1.047m-1.121.602c-.684.378-1.139.337-1.493.565-.3.193-.122.61-.34.76-.269.186-1.061-.012-2.085.326-.845.279-1.384 1.082-2.903 1.082s-2.045-.801-2.904-1.084c-1.022-.338-1.816-.14-2.084-.325-.218-.15-.041-.568-.341-.761-.354-.228-.809-.187-1.492-.563-.436-.24-.189-.39-.044-.46 2.478-1.199 2.873-3.05 2.89-3.188.022-.166.045-.297-.138-.466-.177-.164-.962-.65-1.18-.802-.36-.252-.52-.503-.402-.812.082-.214.281-.295.49-.295a1 1 0 0 1 .197.022c.396.086.78.285 1.002.338q.04.01.082.011c.118 0 .16-.06.152-.195-.026-.433-.087-1.277-.019-2.066.094-1.084.444-1.622.859-2.097.2-.229 1.137-1.22 2.93-1.22 1.792 0 2.732.987 2.931 1.215.416.475.766 1.013.859 2.098.068.788.009 1.632-.019 2.065-.01.142.034.195.152.195a.4.4 0 0 0 .082-.01c.222-.054.607-.253 1.002-.338a1 1 0 0 1 .197-.023c.21 0 .409.082.49.295.117.309-.04.56-.401.812-.218.152-1.003.638-1.18.802-.184.169-.16.3-.139.466.018.14.413 1.991 2.89 3.189.147.073.394.222-.041.464"/>
                                    </svg>
                                    <span class="sr-only">Snapchat page</span>
                                </a>
                                <a href="https://www.instagram.com/escapetime.sa?stkn=ZzNhMThnaWh5YWdy&utm_source=qr" target="_blank" rel="noopener" class="text-white hover:text-stone-300">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" class="w-4 h-4" viewBox="0 0 16 16">
                                        <path d="M8 0C5.829 0 5.556.01 4.703.048 3.85.088 3.269.222 2.76.42a3.917 3.917 0 0 0-1.417.923A3.927 3.927 0 0 0 .42 2.76C.222 3.268.087 3.85.048 4.7.01 5.555 0 5.827 0 8.001c0 2.172.01 2.444.048 3.297.04.852.174 1.433.372 1.942.205.526.478.972.923 1.417.444.445.89.719 1.416.923.51.198 1.09.333 1.942.372C5.555 15.99 5.827 16 8 16s2.444-.01 3.298-.048c.851-.04 1.434-.174 1.943-.372a3.916 3.916 0 0 0 1.416-.923c.445-.445.718-.891.923-1.417.197-.509.332-1.09.372-1.942C15.99 10.445 16 10.173 16 8s-.01-2.445-.048-3.299c-.04-.851-.175-1.433-.372-1.941a3.926 3.926 0 0 0-.923-1.417A3.911 3.911 0 0 0 13.24.42c-.51-.198-1.092-.333-1.943-.372C10.443.01 10.172 0 7.998 0h.003zm-.717 1.442h.718c2.136 0 2.389.007 3.232.046.78.035 1.204.166 1.486.275.373.145.64.319.92.599.28.28.453.546.598.92.11.281.24.705.275 1.485.039.843.047 1.096.047 3.231s-.008 2.389-.047 3.232c-.035.78-.166 1.203-.275 1.485a2.47 2.47 0 0 1-.599.919c-.28.28-.546.453-.92.598-.28.11-.704.24-1.485.276-.843.038-1.096.047-3.232.047s-2.39-.009-3.233-.047c-.78-.036-1.203-.166-1.485-.276a2.478 2.478 0 0 1-.92-.598 2.48 2.48 0 0 1-.6-.92c-.109-.281-.24-.705-.275-1.485-.038-.843-.046-1.096-.046-3.233 0-2.136.008-2.388.046-3.231.036-.78.166-1.204.276-1.486.145-.373.319-.64.599-.92.28-.28.546-.453.92-.598.282-.11.705-.24 1.485-.276.738-.034 1.024-.044 2.515-.045v.002zm4.988 1.328a.96.96 0 1 0 0 1.92.96.96 0 0 0 0-1.92zm-4.27 1.122a4.109 4.109 0 1 0 0 8.217 4.109 4.109 0 0 0 0-8.217zm0 1.441a2.667 2.667 0 1 1 0 5.334 2.667 2.667 0 0 1 0-5.334z"/>
                                    </svg>
                                    <span class="sr-only">Instagram page</span>
                                </a>
                                <a href="https://www.tiktok.com/@escapetime.sa?_r=1&_t=ZS-99jIqT8WGrH" target="_blank" rel="noopener" class="text-white hover:text-stone-300">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" class="w-4 h-4" viewBox="0 0 16 16">
                                        <path d="M9 0h1.98c.144.715.54 1.617 1.235 2.512C12.895 3.389 13.797 4 15 4v2c-1.753 0-3.07-.814-4-1.829V11a5 5 0 1 1-5-5v2a3 3 0 1 0 3 3z"/>
                                    </svg>
                                    <span class="sr-only">TikTok page</span>
                                </a>
                            </div>
        
                            <!-- Payment methods -->
                            <div class="flex space-x-2 rtl:space-x-reverse">
                                <img :src="'/assets/payments/mada_white.svg'" :alt="'mada_white.png'" class="h-6">
                            </div>
        
                            <div class="flex text-sm text-white sm:text-center dark:text-gray-400 space-x-1 rtl:space-x-reverse">
                                <a href="#">{{ page.props.settings.site_name }}</a> 
                                <p>{{ $t('All Rights Reserved.') }}</p>
                                <p>© {{ new Date().getFullYear() }}</p>
                            </div>
        
                        </div>
                    </div>
                </footer>
                <!-- End Footer -->
        
            </div>
        </div>

        <!-- Learn more -->
        <section id="leran-more" class="text-center flex justify-center items-center" :class="showLeranMore ? 'fixed w-full h-full top-0': 'hidden'">
            <div class="relative">
                <div @click="toggle('leran-more', false)" class="absolute w-full h-screen"></div>
                <div class="p-16 space-y-2">
                    <p class="text-6xl font-bold text-white">{{ trans('Learn more') }}</p>
                    <Carousel :itemsToShow="isMobile ? 1 : 3" :wrapAround="false" :transition="500" :dir="locale == 'en' ? 'ltr' : 'rtl'">
                        <Slide v-for="(item, index) in page.props.faq" :key="item.id">
                            <div class="carousel__item">                            
                                <div class="bg-[#242e30] rounded-lg p-4 w-full">
                                    <div>
                                        <p class="text-white">
                                            <span>{{ JSON.parse(item.question)[locale] }}</span>
                                        </p>
                                        <p class="text-white/70">{{ JSON.parse(item.answer)[locale] }}</p>
                                    </div>
                                </div>
                            </div>

                        </Slide>

                        <template #addons>
                            <Navigation />
                            <Pagination />
                        </template>
                    </Carousel>
                </div>
                <button class="absolute text-white bottom-4 sm:bottom-0" @click="toggle('learn-more', false)">
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" class="bi bi-x-circle-fill" viewBox="0 0 16 16">
                        <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0M5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293z"/>
                    </svg>
                </button>
            </div>
        </section>
        <!-- End Learn more -->

        <!-- Winners -->
        <section id="winners" class="text-center flex justify-center items-center" :class="showWinners ? 'fixed w-full h-full top-0': 'hidden'">
            <div class="relative">
                <div @click="toggle('winners', false)" class="absolute w-full h-screen"></div>
                <div class="p-16 space-y-6">
                    <p class="text-6xl font-bold text-white">{{ trans('Winners') }}</p>
                    <div v-if="page.props.winners.length" class="grid grid-cols-5 lg:grid-cols-4 gap-4 justify-center items-center space-x-4 text-white text-sm leading-6 space-y-3">
                        <div class="relative">
                            <img  v-for="item in page.props.winners" :src="'/winners/' + item.image" :alt="item.image" class="h-[128px] w-[128px] object-cover rounded-full cursor-pointer">
                            <div class="w-full h-full bg-black opacity-0 hover:opacity-35 absolute bottom-0 transform transition duration-300 rounded-lg"></div>
                        </div>
                    </div>
                    <p v-else class="text-xl text-white font-bold">{{ $t('No winners yet') }}</p>
                </div>
                <button class="absolute text-white bottom-4 sm:bottom-0" @click="toggle('winners', false)">
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" class="bi bi-x-circle-fill" viewBox="0 0 16 16">
                        <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0M5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293z"/>
                    </svg>
                </button>
            </div>
        </section>
        <!-- End Winners -->

        <!-- Login -->
        <section id="login" class="text-center flex justify-center items-center" :class="showLogin ? 'fixed w-full h-full top-0': 'hidden'">

            <div class="relative">
                <!-- <div @click="hide" class="absolute w-full h-screen"></div> -->

                <div class="p-16 space-y-6 z-50">

                    <div class="login">
                        <form @submit.prevent="submitLogin" class="space-y-4">
                            <!-- Mobile Number -->
                            <div class="mb-6">
                                <InputLabel for="mobile_number" :value="$t('Mobile Number')" class="text-white mb-3" :class="'!text-lg md:!text-4xl text-start'"/>
                
                                <div class="flex space-x-2 rtl:space-x-reverse">
                                    <PhoneInput id="mobile_number" :codeClass="'bg-[#242e30] p-2 md:p-4 md:pt-7 rounded-2xl md:rounded-3xl !rounded-r-none text-white text-lg md:text-4xl font-revert flex justify-center items-center'" :inputClass="'bg-[#242e30] md:p-4 md:pt-7 rounded-2xl md:rounded-3xl !rounded-l-none text-white text-lg md:text-4xl border-none font-revert focus:outline-none focus:ring-0'" v-model="formLogin.mobile_number"/>
                                    <button class="bg-red-700 rounded-2xl md:rounded-3xl p-2 px-4 md:p-8 text-white" 
                                    :class="{ 
                                        'opacity-25': formLogin.processing,
                                        'opacity-30 pointer-events-none': formLogin.processing
                                    }"
                                    >
                                        <template v-if="formLogin.processing">
                                            <svg class="inline w-12 h-12 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor"/>
                                                <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill"/>
                                            </svg>
                                        </template>
                                        <template v-else>                                            
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16" class="w-6 h-12 md:w-12 rotate-180 rtl:rotate-0">
                                                <path fill-rule="evenodd" d="M12 8a.5.5 0 0 1-.5.5H5.707l2.147 2.146a.5.5 0 0 1-.708.708l-3-3a.5.5 0 0 1 0-.708l3-3a.5.5 0 1 1 .708.708L5.707 7.5H11.5a.5.5 0 0 1 .5.5"/>
                                            </svg>
                                        </template>
                                    </button>
                                </div>
                                
                                <InputError class="mt-2 text-start" :message="formLogin.errors.mobile_number" :class="'!text-xl font-bold'"/>
                            </div>
                            <!-- End Mobile Number -->
                        </form>
                    </div>
                </div>
                <button class="absolute text-white bottom-4 sm:bottom-0" @click="toggle('login', false)">
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" class="bi bi-x-circle-fill" viewBox="0 0 16 16">
                        <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0M5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293z"/>
                    </svg>
                </button>
            </div>
        </section>
        <!-- End Login -->

        <!-- Verify code -->
        <section id="verify-code" class="text-center flex justify-center items-center" :class="showVerifyCode ? 'fixed w-full h-full top-0': 'hidden'">

            <div class="relative">
                <!-- <div @click="hide" class="absolute w-full h-screen"></div> -->

                <div class="p-16 space-y-6 z-50">

                    <div class="verify-code md:w-[500px]">
                        <form @submit.prevent="submitVerifyCode" class="space-y-4">
                            <!-- Verify code -->
                            <div class="mb-6">
                                <InputLabel for="code" :value="$t('Enter the verification code sent to your mobile phone :number', {'number': getMobileNumber(formVerifyCode.mobile_number)})" class="text-white mb-3" :class="'!text-xl text-start'"/>
                
                                <div class="space-y-6">
                                    <div class="dir-ltr">
                                        <InputOTP :length="4" @entered="(v) => formVerifyCode.code = v" :class="'bg-[#242e30] w-20 md:w-28 h-full p-4 rounded-3xl text-center font-sans text-white text-4xl border-none font-revert focus:outline-none focus:ring-0'"/>
                                    </div>

                                    <button class="font-bold text-xl text-white text-start" @click="resendOTP">
                                        {{ $t('Resend OTP') }}
                                    </button>

                                    <button class="bg-red-700 rounded-3xl p-5 w-full text-white text-3xl" 
                                    :class="{ 
                                        'opacity-25': formVerifyCode.processing,
                                        'opacity-30 pointer-events-none': formVerifyCode.processing
                                    }">
                                        <template v-if="formVerifyCode.processing">
                                            {{ $t('Processing...') }}
                                        </template>
                                        <template v-else>                                            
                                            {{ $t('Verify') }}
                                        </template>
                                    </button>
                                </div>
                                
                                <InputError class="mt-2 text-start" :message="formVerifyCode.errors.code" :class="'!text-xl font-bold'"/>
                            </div>
                            <!-- End Mobile Number -->
                        </form>
                    </div>
                </div>
                <button class="absolute text-white bottom-4 sm:bottom-0" @click="toggle('verify-code', false)">
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" class="bi bi-x-circle-fill" viewBox="0 0 16 16">
                        <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0M5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293z"/>
                    </svg>
                </button>
            </div>
        </section>
        <!-- End Verify code -->

        <!-- Register -->
        <section id="register" class="text-center flex justify-center items-center" :class="showRegister ? 'fixed w-full h-full top-0': 'hidden'">

            <div class="relative w-full md:w-[40%]">
                <!-- <div @click="hide" class="absolute w-full h-screen"></div> -->

                <div class="p-14 space-y-6 z-50">

                    <p class="text-2xl font-bold text-white">{{ $t('Register') }}</p>
                    <div class="verify-code">
                        <form @submit.prevent="submitRegister" class="space-y-4 bg-[#242e30] rounded-xl px-6 py-8">

                            <!-- First Name -->
                            <div class="mb-6">
                                <InputLabel for="first_name" :value="$t('First Name')" class="text-white"/>
                
                                <TextInput
                                    id="first_name"
                                    type="text"
                                    class="mt-1 block w-full text-black"
                                    v-model="formRegister.first_name"
                                />
                
                                <InputError class="mt-2" :message="formRegister.errors.first_name" />
                            </div>
                            <!-- End First Name -->
                            
                            <!-- Last Name -->
                            <div class="mb-6">
                                <InputLabel for="last_name" :value="$t('Last Name')" class="text-white"/>
                
                                <TextInput
                                    id="last_name"
                                    type="text"
                                    class="mt-1 block w-full text-black"
                                    v-model="formRegister.last_name"
                                />
                
                                <InputError class="mt-2" :message="formRegister.errors.last_name" />
                            </div>
                            <!-- End Last Name -->

                            <!-- Email removed from public registration -->

                            <div class="mb-6">
                                <InputLabel for="gender" :value="$t('Gender')" />
                                <select id="gender" name="gender" v-model="formRegister.gender" class="border-gray-300 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 focus:border-indigo-500 dark:focus:border-indigo-600 focus:ring-indigo-500 dark:focus:ring-indigo-600 rounded-md shadow-sm mt-1 block w-full">
                                  <option selected disabled>{{ $t('Not selected') }}</option>
                                  <option value="male">{{ $t('Male') }}</option>
                                  <option value="female">{{ $t('Female') }}</option>
                                </select>
                            </div>
                            <!-- End Gender -->

                            <div class="mb-6">
                                <InputLabel for="age" :value="$t('Age')" class="text-white"/>
                
                                <TextInput
                                    id="age"
                                    type="number"
                                    maxlength="2"
                                    class="mt-1 block w-full text-black"
                                    v-model="formRegister.age"
                                />
                
                                <InputError class="mt-2" :message="formRegister.errors.age" />
                            </div>
                            <!-- End Age -->                            

                            <button class="bg-red-700 rounded-3xl p-2 w-full text-white text-xl" 
                            :class="{ 
                                'opacity-25': formRegister.processing,
                                'opacity-30 pointer-events-none': formRegister.processing
                            }">
                                <template v-if="formRegister.processing">
                                    {{ $t('Processing...') }}
                                </template>
                                <template v-else>                                            
                                    {{ $t('Register') }}
                                </template>
                            </button>
                        </form>
                    </div>
                </div>
                <button class="absolute text-white bottom-4 sm:bottom-0" @click="toggle('register', false)">
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" class="bi bi-x-circle-fill" viewBox="0 0 16 16">
                        <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0M5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293z"/>
                    </svg>
                </button>
            </div>
        </section>
        <!-- End Register -->

        <!-- Search -->
        <section id="search" class="text-center flex justify-center items-normal" :class="showSearch ? 'fixed w-full h-full top-0': 'hidden'">

            <div class="relative w-full md:w-[40%]">
                <!-- <div @click="hide" class="absolute w-full h-screen"></div> -->

                <div class="h-screen p-14 space-y-6 z-50" :class="page.props.promotion ? 'pt-24' : ''">
                    <div class="relative flex justify-end items-center bg-[#242e30] !rounded-2xl h-20 px-6 rtl:space-x-reverse">

                        <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" class="text-white w-6" viewBox="0 0 16 16">
                            <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001q.044.06.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1 1 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0"/>
                        </svg> 

                        <!-- Search -->
                        <div class="mb-6">            
                            <TextInput
                                id="search"
                                type="text"
                                class="block w-full text-black"
                                :class="'bg-transparent border-none focus:ring-0 text-white p-4 text-2xl absolute w-full h-full top-0 left-0'"
                                v-model="formSearch.search"
                                :placeholder="$t('Search...')"
                            />
            
                            <InputError class="mt-2" :message="formSearch.errors.search" />
                        </div>
                        <!-- End Search -->
                    </div>
                    <div class="h-full py-12">
                        <div class="overflow-y-auto h-[80%]">
                            <template v-for="item in filteredFAQ">
                                <p class="text-white font-bold">{{ JSON.parse(item.question)[locale] }}</p>
                                <p class="text-white">{{ JSON.parse(item.answer)[locale] }}</p>
                            </template>
                            <p class="text-white font-bold" v-if="filteredFAQ.lengthc">{{ $t('No results.') }}</p>
                        </div>
                        <div class="flex justify-center items-center h-[20%]">
                            <button class="text-white" @click="toggle('search', false)">
                                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" class="bi bi-x-circle-fill" viewBox="0 0 16 16">
                                    <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0M5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293z"/>
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
        <!-- End Search -->
    </div>
</template>
